// Lead Quality Engine — shared hook. Every enquiry form that renders
// <LeadPhoneField engine={...}/> automatically inherits validation,
// analytics, honeypot, cooldown and reCAPTCHA behaviour.
// Future-ready: OTP / mobile-intelligence / AI scoring layers plug into
// guardSubmit() without touching form components.

import { useCallback, useRef, useState } from 'react';
import { validateLeadPhone } from './phoneValidation';
import { getRecaptchaToken } from './recaptcha';
import { isCoolingDown, markSubmitted, COOLDOWN_MESSAGE } from './cooldown';
import { trackPhoneValidation } from '@/services/analytics';

export const PHONE_ERROR_MESSAGE =
  'Please enter a valid, active mobile number so our counsellor can contact you and share your profile evaluation.';

export const useLeadQualityEngine = () => {
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const countryRef = useRef({ iso2: 'in', dialCode: '91', format: undefined });

  const handlePhoneChange = useCallback((value, meta) => {
    setPhone(value || '');
    if (meta && meta.country) {
      countryRef.current = {
        iso2: meta.country.iso2,
        dialCode: meta.country.dialCode,
        format: meta.country.format,
      };
    }
    setPhoneError('');
  }, []);

  const digits = (phone || '').replace(/\D/g, '');
  const dial = String(countryRef.current.dialCode || '');
  const nationalDigits = dial && digits.startsWith(dial) ? digits.slice(dial.length) : digits;

  // Full pre-submission gate: validation → analytics → cooldown → reCAPTCHA.
  // Returns { ok, payloadMeta } — on failure the inline error is set automatically.
  const guardSubmit = useCallback(async () => {
    const country = countryRef.current;
    const result = validateLeadPhone(phone, country);
    trackPhoneValidation(result.valid, {
      country: (country.iso2 || '').toUpperCase(),
      validationResult: result.reason,
    });
    if (!result.valid) {
      setPhoneError(PHONE_ERROR_MESSAGE);
      return { ok: false, message: PHONE_ERROR_MESSAGE };
    }
    if (isCoolingDown()) {
      setPhoneError(COOLDOWN_MESSAGE);
      return { ok: false, message: COOLDOWN_MESSAGE };
    }
    const payloadMeta = {
      phone_validation_status: country.iso2 === 'in' ? 'passed' : 'international_valid',
      website: honeypot,
    };
    const recaptchaToken = await getRecaptchaToken('lead_submit');
    if (recaptchaToken) payloadMeta.recaptcha_token = recaptchaToken;
    return { ok: true, payloadMeta };
  }, [phone, honeypot]);

  const markSubmitSuccess = useCallback(() => markSubmitted(), []);

  // Friendly handling of backend rate-limit responses.
  const interpretResponse = useCallback((res) => {
    if (res && res.status === 429) {
      setPhoneError(COOLDOWN_MESSAGE);
      return COOLDOWN_MESSAGE;
    }
    return null;
  }, []);

  return {
    phone,
    phoneError,
    setPhoneError,
    handlePhoneChange,
    nationalDigits,
    honeypot,
    setHoneypot,
    guardSubmit,
    markSubmitSuccess,
    interpretResponse,
  };
};
